/**
 * Qué ve cada persona del registro: una sola definición para el menú, el
 * buscador, los atajos, la Ayuda y las pruebas por rol.
 *
 * Función pura: recibe lo que la persona puede y devuelve el árbol podado
 * (entradas sin permiso fuera, espacios sin entradas fuera, secciones vacías
 * fuera). Ni los espacios ni las secciones tienen permiso propio.
 */
import type { AppPermission } from '@/types/app-permissions';

import type { Acceso, Elemento, Entrada, Seccion } from './registro';

export interface AccesoDePersona {
  esSuperadmin: boolean;
  tiene: (permiso: AppPermission) => boolean;
  /** Participa de al menos un proyecto (le abre Proyectos sin `projects.view`). */
  participaDeProyectos: boolean;
}

export function puedeVerAcceso(acceso: Acceso, persona: AccesoDePersona): boolean {
  if (acceso === 'todos') return true;
  if (acceso === 'superadmin') return persona.esSuperadmin;
  if (persona.esSuperadmin) return true;
  // Igual que `usePuedeVerEntrada`: la membresía también abre Proyectos.
  if (acceso.permiso === 'projects.view') {
    return persona.participaDeProyectos || persona.tiene('projects.view');
  }
  return persona.tiene(acceso.permiso);
}

export const puedeVerEntrada = (entrada: Entrada, persona: AccesoDePersona): boolean =>
  puedeVerAcceso(entrada.acceso, persona);

export function filtrarRegistro(registro: Seccion[], persona: AccesoDePersona): Seccion[] {
  const salida: Seccion[] = [];
  for (const seccion of registro) {
    const elementos: Elemento[] = [];
    for (const el of seccion.elementos) {
      if (el.tipo === 'entrada') {
        if (puedeVerEntrada(el, persona)) elementos.push(el);
        continue;
      }
      const entradas = el.entradas.filter((e) => puedeVerEntrada(e, persona));
      if (entradas.length > 0) elementos.push({ ...el, entradas });
    }
    if (elementos.length > 0) salida.push({ ...seccion, elementos });
  }
  return salida;
}
